import {
    OTP_FLOW,
    OTP_TYPE_EMAIL,
    OTP_TYPE_SMS,
    OtpType,
    partialSignUpGenericErrorMessage,
    UseCaseKey
} from '~constants/Constants';
import { OnOtpSuccessCallback } from '~domain/entities/OtpStore';
import { MessageGroup, MessageRepository } from '~repositories/MessageRepository';
import { UserRepository } from '~repositories/UserRepository';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';

import { GoToLandingViewUseCase } from './social/GoToLandingViewUseCase';
import { SignUpType } from '~constants/SignUpTypeEnum';
import { toJS } from 'mobx';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { IUserState, UserStatusKey } from '~api/RampPgApiSdk';

export class OtpCheckedUseCase {
    private userRepository: UserRepository;
    private messageRepository: MessageRepository;
    private configRepository: ConfigRepository;

    private otpType: OtpType;
    private signUpType: SignUpType;
    private useCaseKey: UseCaseKey;
    private onOtpSuccess?: OnOtpSuccessCallback;

    // TODO: APACRIGEL-2052 can we get OtpType from ConfigRepository directly?
    constructor(
        otpType: OtpType,
        key: UseCaseKey,
        signUpType: SignUpType,
        onOtpSuccess?: OnOtpSuccessCallback
    ) {
        this.otpType = otpType;
        this.signUpType = signUpType;
        this.useCaseKey = key;
        this.onOtpSuccess = onOtpSuccess;
        this.userRepository = diContainer.get(serviceNames.userRepository);
        this.messageRepository = diContainer.get(serviceNames.messageRepository);
        this.configRepository = diContainer.get(serviceNames.configRepository);
    }
    // TODO: APACRIGEL-2052 separate validation_only mode call
    private isOtpNeeded = async (): Promise<boolean> => {
        const { userFormStore, signUp } = this.userRepository;
        const formValues = toJS(userFormStore.values);
        const { users, success } = await signUp(formValues, true);
        if (!success) {
            // There is API error
            // Set error message which will be displayed on ramp form
            // TODO: APACRIGEL-2052 make sure this is the correct message
            this.messageRepository.setMessages(MessageGroup.SIGN_UP, [
                { transKey: partialSignUpGenericErrorMessage }
            ]);

            return false;
        }

        // check conflict first, can conflict when only 1 existing partial user matched
        if (users.find((user) => user.identifierConflict)) {
            return false;
        }

        const { emailIdentifierFieldName } = this.configRepository;
        const mobileOnlyInPartialSignup =
            emailIdentifierFieldName &&
            !formValues[emailIdentifierFieldName] &&
            this.signUpType === SignUpType.PARTIAL;

        if (
            users.length === 0 ||
            this.hasAllowedSinglePartialUserAccount(users, mobileOnlyInPartialSignup)
        ) {
            return true;
        }

        return !this.hasExistingUserAccount(users);
    };

    // TODO: APACRIGEL-2052 move to UserStateResolverService
    private hasExistingUserAccount(users: IUserState[]) {
        return users?.some(
            ({ userId, userStatusKey }) => userId && userStatusKey.startsWith('existing.')
        );
    }

    private hasAllowedSinglePartialUserAccount(
        users: IUserState[],
        mobileOnlyInPartialSignup: boolean = false
    ) {
        const hasSingleUserAccount =
            new Set(users.filter((user) => user.userId).map((user) => user.userId)).size === 1;

        if (!hasSingleUserAccount) {
            return false;
        }

        // APACRIGEL-3472 mobile only signup and matches PP user, doesn't allow to continue with OTP
        return (
            users[0].userStatusKey === UserStatusKey.EXISTING_PARTIAL ||
            (users[0].userStatusKey === UserStatusKey.EXISTING_PARTIAL_OFFLINE &&
                !mobileOnlyInPartialSignup)
        );
    }

    public execute = async () => {
        const { otpStore } = this.userRepository;
        const errorMessages = this.messageRepository.messages.get(MessageGroup.SIGN_UP);
        this.onOtpSuccess && otpStore.setCallback(this.onOtpSuccess);
        otpStore.setData({
            type: this.otpType?.startsWith(OTP_TYPE_EMAIL) ? OTP_TYPE_EMAIL : OTP_TYPE_SMS
        });

        const needOtp = await this.isOtpNeeded();

        if (needOtp) {
            otpStore.setData({ showOtp: true, flow: OTP_FLOW.SIGN_UP });

            return;
        }
        if (this.useCaseKey === 'social') {
            await new GoToLandingViewUseCase().execute();

            return;
        }
        if (!errorMessages?.length) {
            await this.onOtpSuccess?.();

            return;
        }
    };
}
